import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useCustomer } from "../context/CustomerContext";
import wordmark from "../assets/badge.png";

export default function Nav() {
  const { count, setOpen } = useCart();
  const { customer } = useCustomer();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  const links = [
    { to: "/", label: "Home" },
    { to: "/club", label: "Club" },
    { to: "/shop", label: "Shop" },
    { to: "/story", label: "Our Story" },
    { to: "/testimonials", label: "Testimonials" },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-[100] transition-colors duration-500 ${
        scrolled ? "bg-[#04070A]/70 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="nav-logo">
          <img
            src={wordmark}
            alt="Drift Tackle Co."
            className="h-12 md:h-14 w-auto object-contain mix-blend-screen group-hover:scale-105 transition-transform duration-500"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className="font-mono-plex text-xs tracking-[0.2em] uppercase text-slate-300 hover-underline pb-1"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to={customer ? "/account" : "/login"}
            className="hidden sm:flex items-center gap-2 group"
            data-testid="nav-account-link"
          >
            <User className="w-5 h-5 text-slate-200 group-hover:text-orange-500 transition-colors" />
            <span className="font-mono-plex text-xs tracking-widest uppercase text-slate-300 max-w-[120px] truncate">
              {customer ? (customer.name || "Account") : "Sign In"}
            </span>
          </Link>
          <button
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 group"
            data-testid="nav-cart-button"
          >
            <ShoppingBag className="w-5 h-5 text-slate-200 group-hover:text-orange-500 transition-colors" />
            <span className="hidden sm:inline font-mono-plex text-xs tracking-widest uppercase text-slate-300">
              Cart
            </span>
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 sm:static sm:ml-1 bg-orange-500 text-[#04070A] text-[10px] font-bold w-5 h-5 flex items-center justify-center"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="md:hidden text-slate-200"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#04070A]/95 backdrop-blur-xl border-b border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  className="font-display text-2xl uppercase tracking-tight"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to={customer ? "/account" : "/login"}
                className="font-display text-2xl uppercase tracking-tight text-orange-400"
                data-testid="mobile-nav-account"
              >
                {customer ? "Account" : "Sign In"}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
